import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAppointments } from '../contexts/AppointmentContext';

/**
 * Hook personalizado para gerenciar a busca e filtragem de agendamentos
 * 
 * @param {Object} initialFilters - Filtros iniciais a serem aplicados
 * @returns {Object} Objeto com resultados e funções para manipular a busca
 */
const useAppointmentSearch = (initialFilters = {}) => {
  const { appointments, loading, error } = useAppointments();
  
  // Estado para armazenar os filtros
  const [filters, setFilters] = useState({
    searchTerm: '',
    startDate: null,
    endDate: null,
    status: 'all',  // 'all', 'completed', 'pending', 'canceled'
    professionalId: null,
    serviceId: null,
    ...initialFilters
  });
  
  // Estado para controlar paginação
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10
  });
  
  // Estado para indicar se está realizando uma busca
  const [isSearching, setIsSearching] = useState(false);

  // Aplicar novos filtros (versão memoizada com useCallback)
  const applyFilters = useCallback((newFilters) => {
    if (!newFilters) return;
    
    setIsSearching(true);
    setFilters(prev => ({ ...prev, ...newFilters }));
    // Resetar a paginação ao aplicar novos filtros
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Desativar o indicador de busca após um pequeno delay para feedback visual
    const timer = setTimeout(() => setIsSearching(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // Limpar todos os filtros (versão memoizada com useCallback)
  const clearFilters = useCallback(() => {
    setFilters({
      searchTerm: '',
      startDate: null,
      endDate: null,
      status: 'all',
      professionalId: null,
      serviceId: null
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  // Mudar de página (versão memoizada com useCallback)
  const goToPage = useCallback((pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
  }, []);

  // Mudar itens por página (versão memoizada com useCallback)
  const setItemsPerPage = useCallback((itemsCount) => {
    setPagination({ 
      itemsPerPage: itemsCount,
      currentPage: 1 // Reset para primeira página ao mudar itens por página
    });
  }, []);

  // Filtrar os agendamentos com base nos filtros aplicados
  const filteredAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];
    
    return appointments.filter(appointment => {
      // Filtro por texto (busca em múltiplos campos)
      const matchesSearch = !filters.searchTerm || 
        (appointment.cliente && appointment.cliente.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
        (appointment.whatsapp && appointment.whatsapp.includes(filters.searchTerm)) ||
        (appointment.email && appointment.email.toLowerCase().includes(filters.searchTerm.toLowerCase()));
      
      // Filtro por período
      const appointmentDate = appointment.data ? new Date(appointment.data) : null;
      const matchesStartDate = !filters.startDate || !appointmentDate || 
        new Date(appointmentDate) >= new Date(filters.startDate);
      const matchesEndDate = !filters.endDate || !appointmentDate || 
        new Date(appointmentDate) <= new Date(filters.endDate);
      
      // Filtro por status
      const matchesStatus = filters.status === 'all' || 
        (filters.status === 'completed' && appointment.confirmado) ||
        (filters.status === 'pending' && !appointment.confirmado && !appointment.cancelado) ||
        (filters.status === 'canceled' && appointment.cancelado);
      
      // Filtro por profissional
      const matchesProfessional = !filters.professionalId || 
        appointment.profissionalId === filters.professionalId;
      
      // Filtro por serviço (verifica se algum dos serviços do agendamento corresponde)
      const matchesService = !filters.serviceId || 
        (appointment.servicos && appointment.servicos.some(
          service => service.id === filters.serviceId ||
                    service.serviceId === filters.serviceId
        ));
      
      // Retorna true apenas se todos os filtros derem match
      return matchesSearch && 
             matchesStartDate && 
             matchesEndDate && 
             matchesStatus && 
             matchesProfessional && 
             matchesService;
    });
  }, [appointments, filters]);

  // Calcular os itens da página atual
  const paginatedResults = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    return filteredAppointments.slice(
      startIndex, 
      startIndex + pagination.itemsPerPage
    );
  }, [filteredAppointments, pagination]);
  
  // Atualizar para a primeira página se o número total de páginas diminuir
  useEffect(() => {
    const totalPages = Math.ceil(filteredAppointments.length / pagination.itemsPerPage);
    if (pagination.currentPage > totalPages && totalPages > 0) {
      setPagination(prev => ({ ...prev, currentPage: totalPages }));
    }
  }, [filteredAppointments.length, pagination.currentPage, pagination.itemsPerPage]);

  return {
    // Dados
    searchResults: paginatedResults,
    totalResults: filteredAppointments.length,
    totalAppointments: appointments.length,
    
    // Estado atual
    filters,
    pagination,
    loading,
    error,
    isSearching,
    
    // Funções para manipular a busca
    applyFilters,
    clearFilters,
    goToPage,
    setItemsPerPage,
    
    // Informações de paginação
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    totalPages: Math.ceil(filteredAppointments.length / pagination.itemsPerPage)
  };
};

export default useAppointmentSearch; 